import { observer } from 'mobx-react';
import React, { FC, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStoreContext } from '@stores/rootStore';
import { LFEChart } from '@app/_components/lf-graph/LFEChart';
import { convertSqmToYieldByUnit, displayWeight } from '@farmai-shared/LFUtils';
import { thousandSeparator } from '@services/utils';
import { MockProductionGraph } from './Mock';
import { useRouter } from 'next/navigation';
import { DateTime } from 'luxon';
import { ECharts, TooltipComponentFormatterCallbackParams, ECElementEvent, SeriesOption } from 'echarts';
import Big from 'big.js';
import { Legend } from '@app/_components/lf-graph/Legend';
import { YieldStatsTransformed } from '@services/responseTypes';
import { GraphTooltip } from '@app/_components/weatherModal/month/graphUtils';
import { useLFSearchParams } from '@services/useLFSearchParams';
interface ISeries {
  type: 'bar' | 'line';
  stack?: 'predicted' | 'actual';
  name: string;
  animation?: boolean;
  barGap?: string;
  lineStyle?: object;
  symbol?: string;
  label?: {
    show: boolean;
  };
  data: {
    value: number;
    itemStyle?: object;
    deviationFromTarget?: number;
    deviationFromPrediction?: number;
    averageYield?: number;
  }[];
  itemStyle?: object;
  markLine?: any;
  animationDelay?: number | ((idx: number) => number);
  animationEasing?: 'linear';
  selectedMode?: 'single';
  select?: any;
}
export const ProductionGraph: FC<{
  predicted: YieldStatsTransformed['forecasted'] | undefined;
  actual: YieldStatsTransformed['actual'] | undefined;
  targetMax: number | undefined;
  targetMin: number | undefined;
  mock?: boolean;
  interval?: 'day' | 'week' | 'month';
}> = observer(({
  predicted = {
    total: 0,
    values: []
  },
  actual = {
    total: 0,
    values: []
  },
  targetMax = 1.2,
  targetMin = 0.8,
  mock = false,
  interval = 'month'
}) => {
  const {
    userStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);
  const [screenSize, setScreenSize] = useState<string>('');
  const router = useRouter();
  const {
    searchParams
  } = useLFSearchParams();
  const onChartInit = chart => setChartInstance(chart);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const styles = getComputedStyle(document.documentElement);
      setScreenSize(styles.getPropertyValue('--breakpoint-lg'));
    }
  }, []);
  if (predicted.total === 0 && actual.total === 0) {
    return <MockProductionGraph title={t('noTraceabilityGraphData')} />;
  }
  const tMax = targetMax * userStore.user.weightUnitFactor;
  const tMin = targetMin * userStore.user.weightUnitFactor;
  const borderRadius = 4;
  const weightUnit = userStore.user.settings.weight_unit;
  const areaUnit = userStore.user.settings.area_unit;
  const actualGreen = '#219D78';
  const predictedGreen = '#B3EEDD';
  const targetBlue = '#084C83';
  const targetOrange = '#EE9321';
  const deviationRed = '#FF6261';
  const lang = userStore.user.settings.lang;
  const animate = idx => series[1].data[idx].value ? 1000 : 0;
  const toggleLegendSelection = (chartInstance: ECharts | null, label: string) => {
    if (!chartInstance) return;
    if (label === 'actualProduction') {
      chartInstance.dispatchAction({
        type: 'legendToggleSelect',
        name: t('surplus/deficit')
      });
      chartInstance.dispatchAction({
        type: 'legendToggleSelect',
        name: t('actualProduction')
      });
      return;
    }
    chartInstance.dispatchAction({
      type: 'legendToggleSelect',
      name: t(label)
    });
  };
  const calculateDeviation = (value: number, referenceValue: number) => Big(value).sub(referenceValue).times(-1).toNumber();
  const pushData = (basicSeries: ISeries[], seriesIndex: number, value: number, deviationFromTarget = 0, deviationFromPrediction = 0, averageYield = 0) => {
    const surplusPresent = deviationFromTarget > 0;
    const itemStyle = {
      borderRadius: seriesIndex === 2 || !surplusPresent ? [borderRadius, borderRadius, 0, 0] : [0, 0, 0, 0]
    };
    basicSeries[seriesIndex].data.push({
      value,
      deviationFromTarget,
      deviationFromPrediction,
      itemStyle,
      averageYield
    });
  };
  const iterateValues = (actual: YieldStatsTransformed['actual'], predicted: YieldStatsTransformed['forecasted'], basicSeries: ISeries[]) => {
    actual.values.forEach((d, index) => {
      const actualValue = d.value * userStore.user.weightUnitFactor;
      const predictedValue = predicted.values[index]?.value * userStore.user.weightUnitFactor;
      const averageYield = d.average_yield * userStore.user.weightUnitFactor;
      if (actualValue < tMin) {
        const deficitPrediction = calculateDeviation(predictedValue, actualValue);
        const deviationFromTarget = calculateDeviation(tMin, actualValue);
        pushData(basicSeries, 2, actualValue);
        pushData(basicSeries, 1, 0, deviationFromTarget, predictedValue ? deficitPrediction : 0, averageYield); // Push to seriesIndex 1
      } else if (actualValue > tMax && tMax > 0) {
        const surplus = Big(actualValue).sub(tMax).toNumber();
        const surplusPrediction = Big(actualValue).sub(predictedValue).toNumber();
        pushData(basicSeries, 2, surplus, 0, 0, averageYield);
        pushData(basicSeries, 1, Big(actualValue).sub(surplus).toNumber(), surplus, predictedValue ? surplusPrediction : 0, averageYield); // Push to seriesIndex 1
      } else {
        let deviationFromPrediction = 0;
        deviationFromPrediction = calculateDeviation(predictedValue, actualValue);
        pushData(basicSeries, 2, 0, 0, 0, averageYield);
        pushData(basicSeries, 1, actualValue, 0, deviationFromPrediction, averageYield);
      }
    });
    return basicSeries;
  };
  const formatData = (actual: YieldStatsTransformed['actual'], predicted: YieldStatsTransformed['forecasted']) => {
    const basicSeries: ISeries[] = [{
      type: 'bar',
      stack: 'predicted',
      name: t('predictedProduction'),
      barGap: '-65%',
      data: predicted.values.map(x => ({
        value: x.value * userStore.user.weightUnitFactor,
        itemStyle: {
          borderRadius: [borderRadius, borderRadius, 0, 0]
        }
      })),
      itemStyle: {
        color: predictedGreen
      },
      animationEasing: 'linear'
    }, {
      type: 'bar',
      stack: 'actual',
      name: t('actualProduction'),
      itemStyle: {
        color: actualGreen
      },
      data: [],
      animation: false,
      animationEasing: 'linear'
    }, {
      type: 'bar',
      stack: 'actual',
      name: t('surplus/deficit'),
      data: [],
      itemStyle: {
        color: deviationRed
      },
      animation: false,
      animationEasing: 'linear',
      animationDelay: animate
    }, {
      type: 'line',
      name: t('targetMax'),
      data: actual.values.map(() => ({
        value: tMax
      })),
      lineStyle: {
        color: tMax !== 0 ? targetBlue : 'transparent',
        width: 3,
        length: '100%',
        type: 'dashed'
      },
      symbol: 'none',
      animationEasing: 'linear',
      label: {
        show: false
      }
    }, {
      type: 'line',
      name: t('targetMin'),
      data: actual.values.map(() => ({
        value: tMin
      })),
      lineStyle: {
        color: tMin !== 0 ? targetOrange : 'transparent',
        width: 3,
        type: 'dashed'
      },
      symbol: 'none',
      animationEasing: 'linear',
      label: {
        show: false
      }
    }];
    return iterateValues(actual, predicted, basicSeries);
  };
  const series: ISeries[] = formatData(actual as YieldStatsTransformed['actual'], predicted);
  const createTooltipDiv = (marker: string, label: string, value: number, isYield = false, prefix: boolean) => {
    const stat = isYield ? `${convertSqmToYieldByUnit(value, areaUnit, {
      computed: true
    })} ${t(weightUnit)}/${t(areaUnit)}` : `${thousandSeparator(displayWeight(value, {
      computed: true
    }))} ${t(weightUnit)}`;
    return `
        <div class="flex">
          <div>${marker}</div>
          <div class="w-full flex justify-between">
            <p class="font-light pr-2">${label}</p>
            <p class="min-w-fit whitespace-nowrap">
              ${prefix ? '+' : ''}${stat}
            </p>
          </div>
        </div>`;
  };
  const additionalTooltipData = (marker: string, labelKey: string, value: number | undefined, output: string, t: (labelKey: string) => string, isYield: boolean, createTooltipDiv: (marker: string, label: string, value: number, isYield: boolean, prefix: boolean) => string) => {
    if (value) {
      output += createTooltipDiv(marker, t(labelKey), value, isYield, labelKey === 'deviationFromPrediction' && value >= 0);
    }
    return output;
  };
  const displayTooltipContent = (marker: string, seriesName: string | undefined, value: number, weightUnit: string, t: (labelKey: string) => string) => `<div class="flex">
        <div>${marker}</div>
        <div class="w-full flex justify-between">
          <p class="font-light pr-2">${seriesName}</p>
          <p class="min-w-fit whitespace-nowrap">${thousandSeparator(displayWeight(value, {
    computed: true
  }))} ${t(weightUnit)}</p>
        </div>
      </div>`;
  const toolTipFormatter = (params: TooltipComponentFormatterCallbackParams) => {
    if (!Array.isArray(params)) return '';
    let output = '';
    output += `<p>${params[0].name}</p>`;
    const outputArr = params.reduce((acc: string[], param) => {
      const value = param.seriesIndex === 1 ? actual.values[param.dataIndex].value * userStore.user.weightUnitFactor : (param.data as {
        value: number;
      }).value;
      if (value === 0) return acc;
      if (param.seriesIndex !== 2) {
        if (param.seriesIndex === 0) {
          acc.push(displayTooltipContent(param.marker as string, param.seriesName, value, weightUnit, t));
        } else if (param.seriesIndex !== 0) {
          if (param.seriesName === t('targetMin') && tMin === 0) {
            return acc;
          } else {
            acc.push(displayTooltipContent(param.marker as string, param.seriesName, value, weightUnit, t));
          }
        }
      }
      return acc;
    }, []);
    output += outputArr.join('');
    const seriesData = series[1].data[params[0].dataIndex];
    output = additionalTooltipData(seriesData.deviationFromTarget ? params[2].marker as string : params[1].marker as string, 'averageYield', seriesData.averageYield, output, t, true, createTooltipDiv);
    output = additionalTooltipData(params[2].marker as string, 'deviationFromTarget', seriesData.deviationFromTarget, output, t, false, createTooltipDiv);
    output = additionalTooltipData(params[2].marker as string, 'deviationFromPrediction', seriesData.deviationFromPrediction, output, t, false, createTooltipDiv);
    return output + '</div>';
  };
  const legendData = [{
    label: 'actualProduction',
    svg: <svg width={16} height={16}>
            <defs>
              <linearGradient id="actualGreenDeviationRed" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="50%" stopColor={actualGreen} />
                <stop offset="50%" stopColor={deviationRed} />
              </linearGradient>
            </defs>
            <rect key="actualGreen" width={16} height={16} rx={4} fill="url(#actualGreenDeviationRed)" />
          </svg>,
    extra: <div className="print:hidden">
            <GraphTooltip label="productionGraph_actualAndSurplusDeficit" color={deviationRed} />
          </div>
  }, {
    label: 'predictedProduction',
    svg: <rect key="predictedGreen" width={16} height={16} rx={4} fill={predictedGreen}></rect>,
    extra: <div className="print:hidden">
            <GraphTooltip label="productionGraph_predicted" />
          </div>
  }, {
    label: 'targetMin',
    svg: <line key="target-min" stroke={targetOrange} strokeWidth="3" strokeDasharray="6" x1={0} x2={32} y1={8} y2={8} />,
    extra: <div className="print:hidden">
            <GraphTooltip label="productionGraph_min" />
          </div>
  }, {
    label: 'targetMax',
    svg: <line key="target-max" stroke={targetBlue} strokeWidth="3" strokeDasharray="6" x1={0} x2={32} y1={8} y2={8} />,
    extra: <div className="print:hidden">
            <GraphTooltip label="productionGraph_max" />
          </div>
  }];
  const generateDates = (year: number, month: number, day?: number | null) => {
    const beginning = day ? DateTime.fromObject({
      year,
      month,
      day
    }) : DateTime.fromFormat(`${year}/${month}`, 'yyyy/M');
    return {
      from: beginning.toISO(),
      to: (day ? beginning.endOf('day') : beginning.endOf('month')).toISO()
    };
  };
  const onBarClick = (bar: ECElementEvent) => {
    const datum = actual.values[bar.dataIndex];
    if (!datum.month) return;
    if (interval === 'day' && !datum.day) return;
    const {
      from,
      to
    } = generateDates(datum.year, datum.month, interval === 'day' ? datum.day : undefined);
    let query = `/cropzones?filter[harvesting_date_from]=${DateTime.fromISO(from).toUTC().toISO()}&filter[harvesting_date_to]=${DateTime.fromISO(to).toUTC().toISO()}&filter[status]=all`;
    if (searchParams?.has('filter[crop]')) {
      query += `&filter[crop]=${searchParams.get('filter[crop]')}`;
    }
    if (searchParams?.has('filter[season_id]')) {
      query += `&filter[season]=${searchParams.get('filter[season_id]')}`;
    }
    if (searchParams?.has('filter[label_ids][]')) {
      searchParams?.getAll('filter[label_ids][]').forEach(l => {
        query += `&filter[label_ids][]=${l}`;
      });
    }
    if (bar.seriesIndex !== 0) {
      router.push(query);
    }
  };
  return <div className="flex h-full flex-col">
        <div className="h-144">
          <LFEChart series={series as SeriesOption} onChartInit={onChartInit} tooltip={{
        show: !mock,
        trigger: 'axis',
        formatter: toolTipFormatter,
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        textStyle: {
          color: 'rgb(255,255,255)'
        }
      }} dataZoom={[{
        type: 'slider',
        show: !mock,
        start: 0,
        end: 100,
        filterMode: 'empty',
        showDetail: false
      }]} grid={{
        top: '4%',
        left: '5%',
        right: '2%',
        containLabel: true
      }} xAxis={{
        type: 'category',
        boundaryGap: false,
        data: actual.values.map(x => {
          const locale = lang === 'th' ? 'th' : 'en';
          const buddhistYear = lang === 'th' ? parseInt(String(x.year), 10) + 543 : x.year;
          if (interval === 'day' && x.month && x.day) {
            return DateTime.fromObject({
              year: x.year,
              month: x.month,
              day: x.day
            }).setLocale(locale).toFormat('LLL d');
          }
          if (interval === 'week' && x.week) {
            const monthLabel = x.month ? DateTime.fromObject({
              year: x.year,
              month: x.month
            }).setLocale(locale).toFormat('LLL') : '';
            return `${t('Week')} ${x.week}${monthLabel ? `\n${monthLabel}` : ''}`;
          }
          return `${t(x.label)}\n${buddhistYear}`;
        })
      }} yAxis={{
        type: 'value',
        offset: window.innerWidth < parseInt(screenSize, 10) ? 10 : 30,
        max: value => Math.ceil(Math.max(value.max, tMax) * 1.1),
        axisLabel: {
          formatter: `{value} ${t(weightUnit)}`
        }
      }} events={[{
        eventName: 'click',
        action: onBarClick
      }]} legend={{
        show: false // legend object must exist for custom dispatch action
      }} />
        </div>
        <div className="print:mt-4">
          <Legend event={toggleLegendSelection} data={legendData} chartInstance={chartInstance} />
        </div>
      </div>;
});